import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Button, Link, Stack, TextField } from "@mui/material";
import useAuth from "@/hooks/useAuth";
import { extractErrorMessage, getErrorCode } from "@/utils/errors";
interface LoginFormValues {
  email: string;
  password: string;
}
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: { pathname?: string } } };
  const [serverError, setServerError] = useState<string | null>(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>();
  const onSubmit = async (values: LoginFormValues) => {
    setServerError(null);
    setUnverifiedEmail(null);
    setIsSubmitting(true);
    try {
      await login(values.email, values.password);
      const redirectTo = location.state?.from?.pathname ?? "/dashboard";
      navigate(redirectTo, { replace: true });
    } catch (error) {
      // User Onboarding + Module Access (Pilot) -- a PENDING_VERIFICATION
      // account gets a distinct error_code so we can offer a resend link
      // instead of just the generic "invalid credentials"-style message.
      if (getErrorCode(error) === "UNVERIFIED_ACCOUNT") {
        setUnverifiedEmail(values.email);
      }
      setServerError(extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2}>
        {serverError && <Alert severity="error">{serverError}</Alert>}
        {unverifiedEmail && (
          <Link
            component={RouterLink}
            to={`/resend-verification?email=${encodeURIComponent(unverifiedEmail)}`}
            variant="body2"
          >
            Resend verification email
          </Link>
        )}
        <TextField
          label="Email"
          type="email"
          fullWidth
          autoComplete="email"
          error={Boolean(errors.email)}
          helperText={errors.email?.message}
          {...register("email", { required: "Email is required" })}
        />
        <TextField
          label="Password"
          type="password"
          fullWidth
          autoComplete="current-password"
          error={Boolean(errors.password)}
          helperText={errors.password?.message}
          {...register("password", { required: "Password is required" })}
        />
        <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth>
          {isSubmitting ? "Signing in..." : "Sign In"}
        </Button>
        <Stack direction="row" justifyContent="space-between">
          <Link component={RouterLink} to="/forgot-password" variant="body2">
            Forgot password?
          </Link>
          <Link component={RouterLink} to="/register" variant="body2">
            Create an account
          </Link>
        </Stack>
      </Stack>
    </Box>
  );
}
export default LoginPage;
